/**
 * Typing Speed Test — pure metrics.
 *
 * WPM uses the standard "1 word = 5 characters" convention. Gross WPM counts all
 * typed characters; net WPM counts only correct ones. Kept separate from the
 * React layer so the math is unit-testable.
 */

/** Gross words-per-minute: every typed character counts. */
export function grossWpm(typedChars: number, seconds: number): number {
  if (seconds <= 0 || typedChars <= 0) return 0;
  return typedChars / 5 / (seconds / 60);
}

/** Net words-per-minute: only correctly-typed characters count. */
export function netWpm(correctChars: number, seconds: number): number {
  if (seconds <= 0 || correctChars <= 0) return 0;
  return correctChars / 5 / (seconds / 60);
}

/** Accuracy as a percentage (100 when nothing has been typed yet). */
export function accuracy(correctChars: number, typedChars: number): number {
  if (typedChars <= 0) return 100;
  return (correctChars / typedChars) * 100;
}

export interface CharStats {
  correct: number;
  incorrect: number;
}

/**
 * Compare typed input against the target, character by character. Characters
 * typed past the end of the target count as incorrect (overtyping).
 */
export function countChars(target: string, typed: string): CharStats {
  let correct = 0;
  let incorrect = 0;
  for (let i = 0; i < typed.length; i++) {
    if (i < target.length && typed[i] === target[i]) correct++;
    else incorrect++;
  }
  return { correct, incorrect };
}

export type CharState = "pending" | "correct" | "incorrect" | "current";

/**
 * Per-character render state for the target text given the current typed input.
 * The character at the typed length is "current" (the caret position).
 */
export function charStates(target: string, typed: string): CharState[] {
  return target.split("").map((ch, i) => {
    if (i < typed.length) return typed[i] === ch ? "correct" : "incorrect";
    if (i === typed.length) return "current";
    return "pending";
  });
}
